import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Base64 } from 'js-base64';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserService } from '../../services/user.service';

@Component({
    selector: 'app-nav',
    templateUrl: './nav.component.html',
    styleUrls: ['./nav.component.css'],
    standalone: false
})
export class NavComponent implements OnInit, OnDestroy {

  readonly navLinks = environment.navLinks;
  readonly accountUrl = environment.navLinks.account || `${environment.navLinks.buses}account`;
  showReferralEntry = true;
  activeTab: 'bus' | 'referral' | 'hotel' | 'train' | 'flight' | 'account' = 'flight';
  isLoggedIn = false;
  isMobileViewport = false;
  private destroy$ = new Subject<void>();


  constructor(private router: Router, private userService: UserService) { }

  ngOnInit(): void {
    this.initializeReferralBadge();
    this.syncViewportState();
    this.syncAuthState();
    this.setActiveTabFromUrl(this.router.url);
    this.userService.authState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      });
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.syncAuthState();
        this.setActiveTabFromUrl(event.urlAfterRedirects || event.url);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncViewportState();
  }

  handleAccountClick(event: Event): void {
    event.preventDefault();

    if (this.isLoggedIn) {
      this.activeTab = 'account';
      if (typeof window !== 'undefined') {
        window.location.href = this.accountUrl;
      }
      return;
    }

    const loginTrigger = document.getElementById('openModalStart') as HTMLAnchorElement | HTMLButtonElement | null;
    if (loginTrigger) {
      loginTrigger.click();
      return;
    }

    this.router.navigateByUrl('/home');
  }


  private setActiveTabFromUrl(url: string): void {
    const normalized = (url || '').toLowerCase();
    if (normalized.includes('/referral')) {
      this.activeTab = 'referral';
      return;
    }
    if (normalized.includes('/hotels')) {
      this.activeTab = 'hotel';
      return;
    }
    if (normalized.includes('/trains')) {
      this.activeTab = 'train';
      return;
    }
    if (normalized.includes('/flights')) {
      this.activeTab = 'flight';
      return;
    }
    if (
      normalized.includes('/account') ||
      normalized.includes('/my-trips') ||
      normalized.includes('/my-notifications')
    ) {
      this.activeTab = 'account';
      return;
    }
    this.activeTab = 'flight';
  }

  private syncAuthState(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      this.isLoggedIn = false;
      return;
    }
    this.userService.refreshAuthState();
    this.isLoggedIn = this.userService.isAuthenticated();
  }

  private syncViewportState(): void {
    if (typeof window === 'undefined') {
      this.isMobileViewport = false;
      return;
    }

    this.isMobileViewport = window.innerWidth <= 900;
  }

  logoutUser(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    this.userService.logout();
    this.isLoggedIn = false;
    this.activeTab = 'flight';
    this.router.navigateByUrl('/home');
  }

  private initializeReferralBadge(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      this.showReferralEntry = false;
      return;
    }

    const referralCode = window.localStorage.getItem('referralCode');
    const referralPayload = window.localStorage.getItem('referralPayload');

    this.showReferralEntry = Boolean(referralCode || referralPayload);

    if (!referralPayload) {
      return;
    }

    try {
      const decodedPayload = JSON.parse(Base64.decode(referralPayload));
      if (decodedPayload?.code && !referralCode) {
        window.localStorage.setItem('referralCode', decodedPayload.code);
      }
    } catch {
      this.showReferralEntry = Boolean(referralCode);
    }
  }

}
